import { HTMLProps, PropsWithChildren, memo, useMemo } from 'react';
import { forwardRef } from 'react';
import { cn } from 'utils';

import { ThemeColorKeys } from 'types/ui/Theme';

import { SKey, SKeyLetter } from './styled';

/**
 * Key Button component
 * @param {String} keyName - Small letter indicator for button
 * @param {Boolean} disabled - Status of button
 * @param {String} bgcolor - Background color for button
 * @param {Number} btnSize - Size in pixels
 * @param {Node} children - Any
 */

export type KeyButtonProps = HTMLProps<HTMLButtonElement> & {
  keyName?: string;
  isActive?: boolean;
  disabled?: boolean;
  bgRed?: boolean;
  btnSize?: number;
  round?: boolean;
  m?: string;
} & PropsWithChildren;

const MIN_FONT_SIZE = 16;

const KeyButton = memo(
  forwardRef<HTMLButtonElement, KeyButtonProps>(
    ({ keyName, isActive, disabled, btnSize, bgRed, children, ...props }, ref) => {
      // @ts-expect-error - not sure if this works with "props" in general
      const childrenLength = children?.props?.children?.length;
      const fontSize = useMemo(
        () => Math.min((btnSize ?? 0) / Math.max(childrenLength / 2.5, 4), MIN_FONT_SIZE),
        [btnSize, childrenLength],
      );

      if (!btnSize) return null;

      return (
        <span>
          <SKey
            ref={ref}
            className={cn(
              'border border-border rounded bg-transparent hover:bg-accent',
              bgRed && 'bg-destructive hover:bg-destructive/90 border-destructive',
            )}
            // @ts-expect-error - not sure how to fix this
            withIcon={!!children}
            btnSize={btnSize}
            fontSize={fontSize}
            disabled={disabled}
            active={isActive}
            data-key-name={keyName}
            {...props}
          >
            {children && children}
            {keyName && <SKeyLetter color={ThemeColorKeys.PRIMARY_MAIN}>{keyName}</SKeyLetter>}
          </SKey>
        </span>
      );
    },
  ),
);

KeyButton.displayName = 'KeyButton';

export default KeyButton;
